
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MessageSquare, CheckCircle, AlertCircle, Upload, Loader2 } from "lucide-react";

export default function SubmitTicket() {
  const [formData, setFormData] = useState({
    ticket_type: "technical_issue",
    title: "",
    description: "",
    screenshot_url: ""
  });
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  // Block students from accessing this page
  if (user && user.role === 'user' && user.user_type === 'student') {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            Support ticket submission is not available for students. Please contact your teacher or EA staff for assistance.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setFormData({ ...formData, screenshot_url: file_url });
      setMessage({ type: 'success', text: 'Screenshot uploaded successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: `Failed to upload file: ${err.message}` });
    } finally {
      setUploading(false);
    }
  };

  const submitTicketMutation = useMutation({
    mutationFn: async (data) => {
      return base44.entities.SupportTicket.create({
        ticket_type: data.ticket_type,
        title: data.title,
        description: data.description,
        submitter_name: user?.teacher_full_name || user?.full_name || user?.email,
        submitter_email: user?.email,
        submitter_role: user?.role === 'admin' ? 'admin' : user?.user_type,
        status: 'new',
        priority: 'medium',
        screenshot_url: data.screenshot_url || undefined
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['supportTickets']);
      setMessage({ type: 'success', text: 'Support ticket submitted successfully! Our team will review it shortly.' });
      setFormData({
        ticket_type: "technical_issue",
        title: "",
        description: "",
        screenshot_url: ""
      });
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to submit ticket: ${err.message}` });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Please enter a title' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    if (!formData.description.trim()) {
      setMessage({ type: 'error', text: 'Please enter a description' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    submitTicketMutation.mutate(formData);
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Submit Support Ticket</h1>
            <p className="text-slate-600 mt-1">Report an Issue or Request a New Feature</p>
          </div>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Ticket Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="ticket_type">Ticket Type</Label>
                <Select
                  value={formData.ticket_type}
                  onValueChange={(value) => setFormData({ ...formData, ticket_type: value })}
                >
                  <SelectTrigger id="ticket_type">
                    <SelectValue placeholder="Select ticket type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug_report">🐛 Bug Report</SelectItem>
                    <SelectItem value="technical_issue">⚙️ Technical Issue</SelectItem>
                    <SelectItem value="feature_request">✨ Feature Request</SelectItem>
                    <SelectItem value="other">📝 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="Brief summary of the issue..."
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Please describe the issue or feature request in detail..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="h-40"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Include steps to reproduce (for bugs) or detailed requirements (for features)
                </p>
              </div>

              <div>
                <Label htmlFor="screenshot">Screenshot (Optional)</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    id="screenshot"
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="flex-1"
                  />
                  {uploading && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                </div>
                {formData.screenshot_url && (
                  <div className="mt-2">
                    <img 
                      src={formData.screenshot_url} 
                      alt="Uploaded screenshot" 
                      className="max-w-xs rounded border border-slate-200"
                    />
                  </div>
                )}
              </div>

              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="text-blue-900 text-sm">
                  <strong>📧 What happens next:</strong> Your ticket will be reviewed by our team. We'll investigate the issue and provide updates via email.
                </AlertDescription>
              </Alert>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={submitTicketMutation.isPending}
              >
                {submitTicketMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Submit Ticket
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
